import { alpha, RadioGroup, Stack, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import dayjs from "dayjs";
import { Hourglass, InfoIcon } from "lucide-react";
import {
  useEffect,
  useState } from "react";
import { useTranslation } from "react-i18next";
import type { IEditions } from "types";
import { EditionsContainer } from "./Editions.styles";
import { canSelfEnroll, getEditionJoinAvailability, getEditionMeetingUrl, oneEditionIsEnrolled } from "./utils";
import { ButtonDigited } from "../../../components/Button/Button.variants";
import { TitleAndSubtitle } from "../../../components/Commons/TitleAndSubtitle.component";
import { InfoBanner } from "../../../components/InfoBanner/InfoBanner.component";
import { EditionCard } from "../../../components/ModalEditionChange/EditionCard.component";
import { UnsubscribeEditionConfirmModal } from "../../../components/UnsubscribeEditionConfirmModal";
import { SkeletonCard } from "../../ModalEditionChange/SkeletonCard";

export const Editions = ({
  title,
  description,
  descriptionEnrolled,
  learningObject,
  isVirtual,
  isLoading,
  noPadding=true,
  editionsQueryData
}: IEditions) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t, i18n } = useTranslation();
  const [selectedEditionId, setSelectedEditionId] = useState<number | null>(null);
  const [expandedEditionId, setExpandedEditionId] = useState<number | null>(null);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [showUnsubscribeConfirm, setShowUnsubscribeConfirm] = useState<boolean>(false);
  const [isUnsubscribing, setIsUnsubscribing] = useState<boolean>(false);

  const { selfEnrollment } = learningObject;
  const selfEnrollAllowed = canSelfEnroll(selfEnrollment, learningObject.enrollmentEnrollType);

  const {
    editions,
    handleEditionChange,
    isPollingEditionChange,
    editionsIsFetching,
    refetchEditions
  } = editionsQueryData ?? {};

  const enrolled = oneEditionIsEnrolled(editions);
  const enrolledEdition = editions?.find((edition) => edition.status === "E");
  const isUserEdition = (status: string) => status === "E" || status === "C";
  const hasUserEdition = editions?.some((edition) => isUserEdition(edition.status)) ?? false;
  dayjs.locale(i18n.language);

  const handleEnroll = () => {
    if (selectedEditionId !== null && handleEditionChange) {
      handleEditionChange(selectedEditionId);
    }
  };

  const handleEnterEditMode = () => {
    setSelectedEditionId(enrolledEdition?.id ?? null);
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setSelectedEditionId(enrolledEdition?.id ?? null);
    setExpandedEditionId(null);
  };

  const joinAvailability = enrolledEdition ? getEditionJoinAvailability(enrolledEdition, !!isVirtual) : "not_yet";
  const canJoin = joinAvailability === "available";
  const joinTooltipKey = joinAvailability === "ended" ? "join_no_longer_available" : "join_not_available_yet";
  const handleJoin = () => {
    if (!enrolledEdition) return;
    const url = getEditionMeetingUrl(enrolledEdition);
    if (url) window.open(url, "_blank", "noreferrer");
  };

  const toggleAccordion = (editionId: number) => {
    setExpandedEditionId(expandedEditionId === editionId ? null : editionId);
  };

  const editionsLoaded = editions !== undefined;
  const noEditionsAvailable =
    editionsLoaded && (
      editions.length === 0 ||
      (editions.every((ed) => dayjs(ed.endTime) < dayjs()) && !editions.some((ed) => (ed.status === "E" || ed.status === "C")))
    );
  const showWaitingEnrollment =
    editionsLoaded && !noEditionsAvailable && !selfEnrollAllowed && !hasUserEdition;

  const visibleEditions = (editions ?? [])
    .filter((edition) => {
      if (isUserEdition(edition.status)) return true;
      if (!selfEnrollAllowed || (enrolled && !editMode)) return false;
      return dayjs(edition.endTime) >= dayjs();
    })
    .sort((a, b) => a.startTime - b.startTime);

  const noOtherEnrollableEditions = editMode && enrolled &&
    !visibleEditions.some((edition) => edition.status !== "E");

  const hasOtherEnrollableEditions = (editions ?? []).some(
    (edition) =>
      edition.status !== "E" &&
      edition.status !== "C" &&
      edition.remainingPlaces > 0 &&
      dayjs(edition.endTime) >= dayjs()
  );

  // close edit mode when editions change
  useEffect(() => {
    setEditMode(false);
  }, [editions]);

  return (
    <>
      <TitleAndSubtitle
        title={ title }
        subTitle={ !selfEnrollAllowed ? "" : enrolled && !editMode ? descriptionEnrolled : description }
        sxSubTitle={ {
          marginBottom:"0.75rem"
        } }
      />
      <EditionsContainer
        id="editions-tab-container"
        data-testid="editions-tab-container"
        aria-label={ t("editions_tab_aria_label") }
        noPadding={ noPadding }
      >
        { noEditionsAvailable &&
          <div style={ { marginBottom: "1rem" } } data-testid="editions-empty-state">
            <InfoBanner
              id="editions-empty"
              ariaLabel={ t("editions_empty_banner_aria_label") }
              backgroundColor={ theme.customColors.backgroundSecondaryAlternative }
              textColor={ theme.customColors.textTag }
              borderColor={ theme.customColors.backgroundAccent }
              icon={ <InfoIcon size={ "1.5rem" } color={ theme.customColors.textTag } /> }
              title={ t("info_banner_title_editions") }
              label={ t("info_banner_description_editions") }
            />
          </div> }
        { showWaitingEnrollment && (
          <Stack
            data-testid="editions-waiting-enrollment"
            sx={ { marginBottom: "1rem" } }
          >
            <InfoBanner
              id="editions-waiting-enrollment"
              ariaLabel={ t("editions_waiting_enrollment_aria_label") }
              backgroundColor={ theme.customColors.backgroundSecondaryAlternative }
              textColor={ theme.customColors.textTag }
              borderColor={ theme.customColors.backgroundAccent }
              icon={ <Hourglass size={ "1.25rem" } color={ theme.customColors.textTag } /> }
              label={ t("waiting_enrollment", "A breve verrai iscritto a un'edizione dell'aula virtuale") }
              sxContainer={ {
                padding: "0.5rem 1rem"
              } }
            />
          </Stack>
        ) }
        { isLoading || isPollingEditionChange || editionsIsFetching ? <SkeletonCard />
          : (
            <>
              <RadioGroup
                id="editions-radio-group"
                data-testid="editions-radio-group"
                aria-label={ t("editions_radio_group_aria_label") }
                value={ selectedEditionId }
                onChange={ (e) => setSelectedEditionId(Number(e.target.value)) }
                sx={ {
                  maxHeight: "30rem",
                  overflowY: "auto",
                  flexDirection: "column",
                  flexWrap: "nowrap",
                  "::-webkit-scrollbar": {
                    width: "0.44rem",
                    backgroundColor: `${alpha(theme.customColors.exploreCardText, 0.2)}`,
                    borderRadius: "0.5rem"
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: `${alpha(theme.customColors.exploreCardText, 0.2)}!important`,
                    borderRadius: "0.5rem"
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: `${alpha(theme.customColors.exploreCardText, 1)}!important`,
                    borderRadius: "0.5rem"
                  }
                } }
              >
                { visibleEditions.map((edition, idx) => (
                  <EditionCard
                    key={ edition.id + idx }
                    learningObject={ learningObject }
                    edition={ edition }
                    editMode={ editMode }
                    isVirtual={ !!isVirtual }
                    isExpanded={ expandedEditionId === edition.id }
                    onToggleAccordion={ () => toggleAccordion(edition.id) }
                    oneEditionIsEnrolled={ enrolled }
                    theme={ theme }
                    t={ t }
                  />
                )) }

              </RadioGroup>
              { isVirtual && enrolledEdition && (
                <Tooltip
                  placement={ isMobile ? "bottom" : "right" }
                  title={ canJoin ? "" : t(joinTooltipKey) }
                >
                  <span style={ { display: "inline-flex" } }>
                    <ButtonDigited
                      id="editions-join-button"
                      data-testid="editions-join-button"
                      aria-label={ t("join") }
                      typology="primary"
                      disabled={ !canJoin }
                      onClick={ handleJoin }
                    >
                      { t("join") }
                    </ButtonDigited>
                  </span>
                </Tooltip>
              ) }
              { noOtherEnrollableEditions && (
                <div style={ { marginBottom: "1rem" } } data-testid="editions-no-other-editions">
                  <InfoBanner
                    id="editions-no-other-editions"
                    ariaLabel={ t("no_enrollable_editions") }
                    backgroundColor={ theme.customColors.backgroundSecondaryAlternative }
                    textColor={ theme.customColors.textTag }
                    borderColor={ theme.customColors.backgroundAccent }
                    icon={ <InfoIcon size={ "1.5rem" } color={ theme.customColors.textTag } /> }
                    label={ t("no_enrollable_editions") }
                  />
                </div>
              ) }
              { enrolled && selfEnrollAllowed && (
                <Stack direction="row" gap={ 1 } alignItems="center">
                  { !editMode ? (
                    <Tooltip
                      placement={ isMobile ? "bottom" : "right" }
                      title={ !hasOtherEnrollableEditions ? t("no_enrollable_editions") : "" }
                    >
                      <span style={ { display: "inline-flex" } }>
                        <ButtonDigited
                          id="editions-modify-button"
                          data-testid="editions-modify-button"
                          aria-label={ t("editions_modify_button_aria_label") }
                          typology="secondary"
                          disabled={ !hasOtherEnrollableEditions }
                          onClick={ handleEnterEditMode }
                        >
                          { t("modify") }
                        </ButtonDigited>
                      </span>
                    </Tooltip>
                  ) : (
                    <ButtonDigited
                      id="editions-save-button"
                      data-testid="editions-save-button"
                      aria-label={ t("editions_save_button_aria_label") }
                      typology="secondary"
                      onClick={ handleEnroll }
                    >
                      { t("editions_save_button") }
                    </ButtonDigited>
                  ) }
                  { enrolledEdition && (
                    <ButtonDigited
                      id="editions-unsubscribe-button"
                      data-testid="editions-unsubscribe-button"
                      aria-label={ t("unsubscribe") }
                      typology="tertiary"
                      disabled={ isUnsubscribing || isPollingEditionChange || editionsIsFetching }
                      onClick={ () => setShowUnsubscribeConfirm(true) }
                    >
                      { isUnsubscribing ? t("unsubscribing") : t("unsubscribe") }
                    </ButtonDigited>
                  ) }
                  { editMode && (
                    <ButtonDigited
                      id="editions-cancel-edit-button"
                      data-testid="editions-cancel-edit-button"
                      aria-label={ t("annul") }
                      typology="tertiary"
                      disabled={ isPollingEditionChange || editionsIsFetching }
                      onClick={ handleCancelEdit }
                    >
                      { t("annul") }
                    </ButtonDigited>
                  ) }
                </Stack>
              ) }

              { /* utente non enrolled, puo' iscriversi */ }
              { !enrolled && selfEnrollAllowed && !editMode && (
                <ButtonDigited
                  id="editions-enroll-button"
                  data-testid="editions-enroll-button"
                  aria-label={ t("editions_enroll_button_aria_label") }
                  typology="primary"
                  disabled={ selectedEditionId === null || isLoading || isPollingEditionChange }
                  onClick={ handleEnroll }
                >
                  { t("enroll_edition") }
                </ButtonDigited>
              ) }
            </>
          )
        }
      </EditionsContainer>
      { enrolledEdition && (
        <UnsubscribeEditionConfirmModal
          editionId={ enrolledEdition.id }
          objectId={ learningObject.id }
          open={ showUnsubscribeConfirm }
          onClose={ () => setShowUnsubscribeConfirm(false) }
          onSuccess={ () => refetchEditions?.() }
          onUnsubscribingChange={ setIsUnsubscribing }
          title={ t("unsubscribe_edition_confirm_title") }
          description=""
          closeOnSuccess
        />
      ) }
    </>
  );
};
